import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../Sidebar';
import Header from '../Header';
import axios from 'axios';
import { usePermissions } from '../context/PermissionsContext';

interface Consultant {
  id: string;
  name: string;
  surname: string;
  id_passport_number: string;
  contact_number: string;
  email: string;
  active: boolean;
  verified: boolean;
  start_date: string;
  code: string;
  work_model: string;
  position: string;
  role: string;
}

interface NewConsultantForm {
  name: string;
  surname: string;
  id_passport_number: string;
  contact_number: string;
  email: string;
  password: string;
  start_date: string;
  code: string;
  work_model: string;
  position: string;
  role: string;
}

const API_URL = process.env.REACT_APP_API_URL;

const ConsultantsPage = () => {
  const { can } = usePermissions();
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [filteredConsultants, setFilteredConsultants] = useState<Consultant[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<NewConsultantForm>({
    name: '',
    surname: '',
    id_passport_number: '',
    contact_number: '',
    email: '',
    password: '',
    start_date: '',
    code: '',
    work_model: '',
    position: '',
    role: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<NewConsultantForm>>({});

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    const fetchConsultants = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        const response = await fetch(`${API_URL}/api/consultants`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        console.log('📊 Response status:', response.status, response.statusText);

        // Clone response to allow multiple reads
        const responseClone = response.clone();
        const contentType = response.headers.get('content-type');

        if (!response.ok) {
          let errorMessage = `Server error: ${response.status} ${response.statusText}`;
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } else {
            const errorText = await responseClone.text();
            errorMessage = errorText || errorMessage;
          }
          throw new Error(errorMessage);
        }

        if (!contentType || !contentType.includes('application/json')) {
          const errorText = await responseClone.text();
          throw new Error(`Unexpected response format: ${errorText || 'No data'}`);
        }

        const data = await response.json();
        console.log('✅ Consultants data received:', data);
        if (data.success) {
          const consultantsData = data.data || [];
          setConsultants(consultantsData);
          setFilteredConsultants(consultantsData);
          console.log(`📋 Loaded ${consultantsData.length || 0} consultants`);
        } else {
          throw new Error(data.message || 'Failed to fetch consultants');
        }
      } catch (error) {
        console.error('❌ Error fetching consultants:', error);
        setError(error instanceof Error ? error.message : 'Failed to load consultants');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConsultants();
  }, []);

  // Filter consultants based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredConsultants(consultants);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = consultants.filter(consultant => 
        consultant.name?.toLowerCase().includes(lowercasedSearch) ||
        consultant.surname?.toLowerCase().includes(lowercasedSearch) ||
        consultant.email?.toLowerCase().includes(lowercasedSearch) ||
        `${consultant.name} ${consultant.surname}`.toLowerCase().includes(lowercasedSearch)
      );
      setFilteredConsultants(filtered);
    }
  }, [searchTerm, consultants]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for the field
    setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Validate form
  const validateForm = () => {
    const errors: Partial<NewConsultantForm> = {};
    if (!formData.surname) errors.surname = 'Surname is required';
    if (!formData.email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email format';
    if (!formData.password) errors.password = 'Password is required';
    else if (formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  const errors = validateForm();
  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
    return;
  }
  try {
    const token = localStorage.getItem('token');
    console.log('API_URL:', API_URL, 'Token:', token, 'Form data:', formData);
    const response = await axios.post(`${API_URL}/api/consultants`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    console.log('Response:', response.data);
    if (response.status === 201) {
        setConsultants(prev => [...prev, response.data.data]);
        setSuccessMessage('Consultant created successfully!');
        setIsModalOpen(false); // Ensure modal closes
        setTimeout(() => setSuccessMessage(null), 3000);
        setFormData({
          name: '',
          surname: '',
          id_passport_number: '',
          contact_number: '',
          email: '',
          password: '',
          start_date: '',
          code: '',
          work_model: '',
          position: '',
          role: '',
        });
        setFormErrors({});
        // Reset any previous errors
        setError(null);
      } else {
        setError(response.data.message || 'Failed to create consultant');
      }
  } catch (error: any) {
    console.error('Error creating consultant:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      fullResponse: error.response,
    });

    // Get the error message from the response
  const errorMessage = error.response?.data?.message || error.message || 'Failed to create consultant';
  
  // Log the full error message
  console.log('Full error message:', errorMessage);
  console.log('Error data:', error.response?.data);
    
    // Handle duplicate email specifically
    if (error.response?.status === 400 && error.response?.data?.message?.includes('email already exists')) {
      setFormErrors({ email: 'This email is already registered. Please use a different email.' });
      setError(null); // Clear the general error
    } else {
      setError(error.response?.data?.message || 'Failed to create consultant');
    }
  }
};

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />
      {/* Main Content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <Header
          title="Consultants"
          user={null}
          client={null}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />
        {/* Page Content */}
        <div className="flex-1 p-6 overflow-auto">
          {error && (
  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
    <span>{error}</span>
    <button
      onClick={() => setError(null)}
      className="absolute top-0 right-0 px-4 py-3"
    >
      <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
)}

{successMessage && (
  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
    <span>{successMessage}</span>
    <button
      onClick={() => setSuccessMessage(null)}
      className="absolute top-0 right-0 px-4 py-3"
    >
      <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
)}

          <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-[1600px] mx-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Consultants</h2>
              {can('consultants', 'write') && (
                <button
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  onClick={() => setIsModalOpen(true)}
                >
                  Create New Consultant
                </button>
              )}
            </div>

            {/* Search Field */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <input
                  type="text"
                  placeholder="Search Consultants by name or email..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              {searchTerm && (
                <p className="text-sm text-gray-600 mt-2">
                  Showing {filteredConsultants.length} of {consultants.length} consultants
                </p>
              )}
            </div>

            {isLoading ? (
              <p className="text-gray-700">Loading consultants...</p>
            ) : filteredConsultants.length === 0 ? (
              <div className="text-center py-8">
                {searchTerm ? (
                  <p className="text-gray-700">No consultants found matching "{searchTerm}"</p>
                ) : (
                  <p className="text-gray-700">No consultants available.</p>
                )}
              </div>
            )
             : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID/Passport Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Active
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Verified
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredConsultants.map((consultant) => (
                      <tr key={consultant.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <Link
                            to={`/consultants/${consultant.id}`}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {consultant.name}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {consultant.id_passport_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {consultant.contact_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {consultant.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {consultant.active ? 'Yes' : 'No'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {consultant.verified ? 'Yes' : 'No'}
                        </td>
                      </tr>
                    ))}
                  </tbody>

                </table>
              </div>
            )}
          </div>
          {isModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="bg-white p-6 rounded-lg max-w-3xl w-full">
                <h2 className="text-xl font-bold mb-4">Create New Consultant</h2>
                <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full border p-2 rounded"
                      placeholder="Enter name (optional)"
                    />
                    {formErrors.name && <p className="text-red-500 text-sm">{formErrors.name}</p>}
                  </div>
                  <div>
                    <label className="block mb-1">Surname *</label>
                    <input
                      type="text"
                      name="surname"
                      value={formData.surname}
                      onChange={handleInputChange}
                      className="w-full border p-2 rounded"
                      placeholder="Enter surname"
                    />
                    {formErrors.surname && <p className="text-red-500 text-sm">{formErrors.surname}</p>}
                  </div>
                  <div>
                    <label className="block mb-1">ID/Passport Number</label>
                    <input
                      type="text"
                      name="id_passport_number"
                      value={formData.id_passport_number}
                      onChange={handleInputChange}
                      className="w-full border p-2 rounded"
                      placeholder="Enter ID or passport number"
                    />
                  </div>
                  <div>
                    <label className="block mb-1">Contact Number</label>
                    <input
                      type="text"
                      name="contact_number"
                      value={formData.contact_number}
                      onChange={handleInputChange}
                      className="w-full border p-2 rounded"
                      placeholder="Enter contact number"
                    />
                  </div>
                  <div>
                    <label className="block mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full border p-2 rounded"
                      placeholder="Enter email"
                    />
                    {formErrors.email && <p className="text-red-500 text-sm">{formErrors.email}</p>}
                  </div>
                  <div>
                    <label className="block mb-1">Password *</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full border p-2 rounded"
                      placeholder="Enter password"
                    />
                    {formErrors.password && <p className="text-red-500 text-sm">{formErrors.password}</p>}
                  </div>
                  <div>
                    <label className="block mb-1">Start Date</label>
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      className="w-full border p-2 rounded"
                    />
                  </div>
                  <div>
                    <label className="block mb-1">Code</label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      className="w-full border p-2 rounded"
                      placeholder="Enter code"
                    />
                  </div>
                  <div>
                    <label className="block mb-1">Work Model</label>
                    <select
                      name="work_model"
                      value={formData.work_model}
                      onChange={handleInputChange}
                      className="w-full border p-2 rounded"
                    >
                      <option value="">Select work model</option>
                      <option value="Remote">Remote</option>
                      <option value="Onsite">Onsite</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>

                  <div>
                    <label className="block mb-1">Position</label>
                    <select
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className="w-full border p-2 rounded"
                    >
                      <option value="">Select position</option>
                      <option value="Admin Consultant">Admin Consultant</option>
                      <option value="Assistant">Assistant</option>
                      <option value="Business Owner">Business Owner</option>
                      <option value="Claims Consultant">Claims Consultant</option>
                      <option value="Consultant">Consultant</option>
                      <option value="Trainer">Trainer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block mb-1">Role</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="w-full border p-2 rounded"
                    >
                      <option value="">Select role</option>
                      <option value="Admin Consultant">Admin Consultant</option>
                      <option value="Assistant">Assistant</option>
                      <option value="Consultant">Consultant</option>
                      <option value="Claims Consultant">Claims Consultant</option>
                      <option value="External Consultant">External Consultant</option>
                    </select>
                  </div>

                  <div className="mt-4 flex justify-end space-x-2">
    <button
      type="button"
      className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
      onClick={() => setIsModalOpen(false)}
    >
      Cancel
    </button>
    <button
      type="submit"
      className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
    >
      Submit
    </button>
  </div>
</form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultantsPage;